import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Location,
  Navigate,
  Navigator,
  Path,
  Routes,
  To,
  UNSAFE_NavigationContext as NavigationContext,
  parsePath,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Drawer from "Components/UI/Drawer/Drawer";
import { CONTACTS, USERS } from "Shared/Constants/routes";

import { RouteDrawerContext, RouteDetailContext } from "./RouteDrawerContext";
import {
  RouteDrawerMatch,
  findRouteDrawer,
  isSamePath,
  toDrawerUrl,
} from "./routeDrawers";
import styles from "./RouteDrawer.module.scss";
import { findRouteDetail } from "./routeDetails";

type Search = Pick<Path, "search" | "hash">;

const toPath = (to: To): Partial<Path> =>
  typeof to === "string" ? parsePath(to) : to;

/** A link to a create form goes to its list with `?new=1` instead. */
const toDrawerTarget = (to: To): To => {
  const path = toPath(to);
  if (!path.pathname) return to;
  const url = toDrawerUrl(path.pathname, path.search ?? "");
  return url ? { ...parsePath(url), hash: path.hash ?? "" } : to;
};

const withNavigator = (
  navigation: React.ContextType<typeof NavigationContext>,
  navigator: Navigator,
) => ({ ...navigation, navigator });

/**
 * Renders the app's routes. Create forms (see ROUTE_DRAWERS) open in a
 * side drawer over their list, at the list's URL plus `?new=1`, e.g.
 * `/fuel-history?new=1`. Links to the old `/…/create` paths still work.
 */
const RoutedPages = ({ routes }: { routes: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useContext(NavigationContext);

  // Old create URLs (bookmarks, full page loads) move to the list's URL.
  const legacyUrl = toDrawerUrl(location.pathname, location.search);
  const createDrawer = legacyUrl
    ? null
    : findRouteDrawer(location.pathname, location.search);
  const detail =
    legacyUrl || createDrawer
      ? null
      : findRouteDetail(location.pathname, location.search);
  const drawer = createDrawer ?? detail;

  // Every navigation in the app goes to the list URL, never `/create`.
  const appNavigation = useMemo(() => {
    const { navigator } = navigation;
    const detailState = (to: To, state: unknown) => {
      const target = toPath(to);
      const match =
        target.pathname && findRouteDetail(target.pathname, target.search);
      if (!match) return state;
      const currentState = location.state as {
        routeDetailBackground?: Location;
      } | null;
      const background = isSamePath(location.pathname, match.parentPath)
        ? location
        : currentState?.routeDetailBackground;
      return background && isSamePath(background.pathname, match.parentPath)
        ? {
            ...(typeof state === "object" && state !== null ? state : {}),
            routeDetailBackground: background,
          }
        : state;
    };
    return withNavigator(navigation, {
      ...navigator,
      createHref: (to: To) => navigator.createHref(toDrawerTarget(to)),
      push: (to: To, state?: unknown, options?: never) =>
        navigator.push(toDrawerTarget(to), detailState(to, state), options),
      replace: (to: To, state?: unknown, options?: never) =>
        navigator.replace(toDrawerTarget(to), detailState(to, state), options),
    });
  }, [navigation, location]);

  // The list the user came from, with its filters and page, sits behind
  // the drawer. Opened from a link elsewhere, the plain list does.
  const lastPage = useRef<Location>(location);
  if (!drawer && !legacyUrl) lastPage.current = location;

  // Search params the list writes while behind the drawer (lists often
  // set their default filters on mount) apply to the list only.
  const [backgroundSearch, setBackgroundSearch] = useState<Search | null>(null);
  const drawerKey = drawer ? `${drawer.parentPath}|${drawer.formPath}` : null;
  useEffect(() => setBackgroundSearch(null), [drawerKey]);

  // After the drawer closes the list remounts, so a record just created
  // shows up without a manual refresh.
  const visits = useRef(0);
  const wasOpen = useRef<"create" | "detail" | null>(null);
  const drawerKind = drawer ? (detail ? "detail" : "create") : null;
  if (wasOpen.current === "create" && !drawer && !legacyUrl) {
    visits.current += 1;
  }
  wasOpen.current = drawerKind;

  let background: Location | undefined;
  let formLocation: Location | undefined;
  if (drawer) {
    const savedBackground = (
      location.state as { routeDetailBackground?: Location } | null
    )?.routeDetailBackground;
    const previous =
      detail &&
      savedBackground &&
      isSamePath(savedBackground.pathname, drawer.parentPath)
        ? savedBackground
        : lastPage.current;
    const contactsOrigin =
      detail &&
      drawer.parentPath === USERS &&
      isSamePath(previous.pathname, CONTACTS);
    const base: Location =
      isSamePath(previous.pathname, drawer.parentPath) || contactsOrigin
        ? previous
        : {
            pathname: drawer.parentPath,
            search: "",
            hash: "",
            state: null,
            key: "route-drawer-background",
          };
    background = backgroundSearch ? { ...base, ...backgroundSearch } : base;
    formLocation = {
      pathname: drawer.formPath,
      search: drawer.formSearch,
      hash: location.hash,
      state: location.state,
      key: location.key,
    };
  }

  // While the drawer slides out, keep showing the form it had.
  const lastDrawer = useRef<{
    match: RouteDrawerMatch;
    location: Location;
    detail: boolean;
  }>();
  if (drawer && formLocation) {
    lastDrawer.current = {
      match: drawer,
      location: formLocation,
      detail: Boolean(detail),
    };
  }

  const backgroundPath = background?.pathname;
  const backgroundNavigation = useMemo(() => {
    const { navigator } = appNavigation;
    if (!backgroundPath) return appNavigation;
    // Stay on the list's own path: update the list, not the URL.
    const handled = (to: To) => {
      const path = toPath(to);
      if (path.pathname && !isSamePath(path.pathname, backgroundPath)) {
        return false;
      }
      const next = { search: path.search ?? "", hash: path.hash ?? "" };
      setBackgroundSearch((current) =>
        current?.search === next.search && current?.hash === next.hash
          ? current
          : next,
      );
      return true;
    };
    return withNavigator(appNavigation, {
      ...navigator,
      push: (to: To, state?: unknown, options?: never) => {
        if (!handled(to)) navigator.push(to, state, options);
      },
      replace: (to: To, state?: unknown, options?: never) => {
        if (!handled(to)) navigator.replace(to, state, options);
      },
    });
  }, [appNavigation, backgroundPath]);

  if (legacyUrl) {
    return <Navigate to={legacyUrl} replace state={location.state} />;
  }

  const shown = lastDrawer.current;

  const close = () => {
    if (!background) return;
    navigate(`${background.pathname}${background.search}${background.hash}`, {
      state: background.state,
    });
  };

  return (
    <>
      <NavigationContext.Provider value={backgroundNavigation}>
        <Routes key={visits.current} location={background ?? location}>
          {routes}
        </Routes>
      </NavigationContext.Provider>
      {shown && (
        <NavigationContext.Provider value={appNavigation}>
          <Drawer
            open={Boolean(drawer)}
            onClose={close}
            dismissible={shown.detail}
            title={shown.match.config.title}
            width={shown.match.config.width}
            closeLabel={shown.detail ? "Close details" : "Close form"}
          >
            <RouteDrawerContext.Provider value={true}>
              <RouteDetailContext.Provider value={shown.detail}>
                <div className={shown.detail ? styles.detailBody : styles.body}>
                  <Routes
                    key={shown.detail ? shown.location.pathname : undefined}
                    location={shown.location}
                  >
                    {routes}
                  </Routes>
                </div>
              </RouteDetailContext.Provider>
            </RouteDrawerContext.Provider>
          </Drawer>
        </NavigationContext.Provider>
      )}
    </>
  );
};

export default RoutedPages;
